/**
 * useAIWriter - Hook for triggering AI canvas and document operations
 *
 * Provides methods to send AI requests to the Board Durable Object via WebSocket.
 * Used by NodeToolbar and other UI components to create AI-generated content.
 *
 * Features:
 * - Canvas operations: Create AI-generated sticky notes, shapes, diagrams
 * - Document operations: Rewrite, summarize, expand, simplify text
 * - Uses existing WebSocket connection from useYjsProvider
 *
 * @example
 * ```typescript
 * const { sendCanvasRequest, sendDocumentRequest } = useAIWriter({
 *   boardId: 'my-board',
 *   wsRef: websocketRef,
 * });
 *
 * // Create AI sticky note at viewport center
 * sendCanvasRequest({
 *   action: 'generate',
 *   context: 'Ideas for a marketing campaign',
 *   position: { x: 500, y: 300 },
 *   nodeType: 'sticky',
 * });
 *
 * // Rewrite selected text in a document
 * sendDocumentRequest({
 *   action: 'rewrite',
 *   context: selectedText,
 *   targetField: 'richtext',
 * });
 * ```
 */

import { useCallback, type RefObject } from 'react';
import type { AIRequestMessage } from '@deep-agents/shared-types';

// ============================================================================
// TYPES
// ============================================================================

/**
 * Options for the useAIWriter hook.
 */
export interface UseAIWriterOptions {
  /** Board ID for context */
  boardId: string;
  /** Reference to the WebSocket connection from useYjsProvider */
  wsRef: RefObject<WebSocket | null>;
}

/**
 * Request to create AI content on the canvas.
 */
export interface AICanvasRequest {
  /** What action the AI should perform */
  action: 'generate' | 'summarize' | 'expand';
  /** User's prompt or context for AI */
  context: string;
  /** Where to create the content on canvas */
  position: { x: number; y: number };
  /** Type of node to create (default: sticky) */
  nodeType?: 'sticky' | 'shape';
  /** Shape type if nodeType is 'shape' */
  shapeType?: 'rectangle' | 'circle' | 'triangle';
}

/**
 * Request to transform text in a document.
 */
export interface AIDocumentRequest {
  /** What action the AI should perform */
  action: 'generate' | 'rewrite' | 'summarize' | 'expand' | 'simplify' | 'formal' | 'casual';
  /** The text to transform */
  context: string;
  /** Y.Text or Y.XmlFragment field to write to */
  targetField: string;
  /** Selection range for partial text operations */
  selectionRange?: { from: number; to: number };
}

/**
 * Return value from useAIWriter hook.
 */
export interface UseAIWriterReturn {
  /** Send a canvas creation request to AI */
  sendCanvasRequest: (request: AICanvasRequest) => void;
  /** Send a document transformation request to AI */
  sendDocumentRequest: (request: AIDocumentRequest) => void;
}

// ============================================================================
// HOOK IMPLEMENTATION
// ============================================================================

/**
 * Hook for sending AI requests via WebSocket to the Board Durable Object.
 *
 * @param options - Hook options (boardId, wsRef)
 * @returns Methods to send canvas and document AI requests
 */
export function useAIWriter({
  boardId,
  wsRef,
}: UseAIWriterOptions): UseAIWriterReturn {

  /**
   * Send a canvas request to create AI-generated content on the whiteboard.
   *
   * The AI will:
   * 1. Receive the request via WebSocket
   * 2. Generate content using AI Gateway
   * 3. Create nodes on the canvas (sticky notes, shapes, etc.)
   * 4. Show AI cursor and typing indicators during creation
   *
   * @param request - Canvas creation request
   */
  const sendCanvasRequest = useCallback(
    (request: AICanvasRequest) => {
      // Validate WebSocket connection
      if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) {
        console.warn('[useAIWriter] WebSocket not connected, cannot send canvas request');
        return;
      }

      // Build the AI request message
      const message: AIRequestMessage = {
        type: 'ai_request',
        action: request.action,
        targetType: 'canvas',
        context: request.context,
        position: request.position,
        // Note: nodeType and shapeType hints are included in context
        // The AI will determine what to create based on the prompt
      };

      // Send via WebSocket as JSON
      wsRef.current.send(JSON.stringify(message));

      console.log('[useAIWriter] Sent canvas request:', {
        action: request.action,
        position: request.position,
        contextPreview: request.context.substring(0, 50) + '...',
      });
    },
    [wsRef]
  );

  /**
   * Send a document request to transform text using AI.
   *
   * The AI will:
   * 1. Receive the request via WebSocket
   * 2. Transform the text using AI Gateway
   * 3. Stream the result character-by-character to the Y.Doc
   * 4. Show AI cursor and typing indicators during writing
   *
   * @param request - Document transformation request
   */
  const sendDocumentRequest = useCallback(
    (request: AIDocumentRequest) => {
      // Validate WebSocket connection
      if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) {
        console.warn('[useAIWriter] WebSocket not connected, cannot send document request');
        return;
      }

      // Build the AI request message
      const message: AIRequestMessage = {
        type: 'ai_request',
        action: request.action,
        targetType: 'document',
        targetField: request.targetField,
        context: request.context,
        // Note: selectionRange could be used for partial text replacement
        // For now, AI replaces/appends to the target field
      };

      // Send via WebSocket as JSON
      wsRef.current.send(JSON.stringify(message));

      console.log('[useAIWriter] Sent document request:', {
        action: request.action,
        targetField: request.targetField,
        contextPreview: request.context.substring(0, 50) + '...',
      });
    },
    [wsRef]
  );

  return {
    sendCanvasRequest,
    sendDocumentRequest,
  };
}
